import React from 'react';
import { Modal, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Basket } from '../../types/basket';

interface ProductAssignmentsModalProps {
  open: boolean;
  basket: Basket | null;
  loading: boolean;
  onCancel: () => void;
}

interface BasketProductAssignmentRow {
  key: string;
  product: string;
  quantityAssigned: number | string;
  associatedProductPresentations: Array<string | number>;
  unit?: string | null;
}

const getBasketProductAssignmentRows = (
  basket?: Basket | Record<string, any> | null
): BasketProductAssignmentRow[] =>
  (basket?.mainProducts ?? []).map((mainProduct: any, index: number) => ({
    key: `${mainProduct.id ?? index}`,
    product: mainProduct.name ?? mainProduct.mainProduct?.name ?? 'N/A',
    quantityAssigned: mainProduct.amount ?? 'N/A',
    associatedProductPresentations: (mainProduct.products ?? []).map((product: any) =>
      typeof product === 'object' ? product.name || product.refId || product.id : product
    ),
    unit: mainProduct.unit,
  }));

const ProductAssignmentsModal: React.FC<ProductAssignmentsModalProps> = ({
  open,
  basket,
  loading,
  onCancel,
}) => {
  const { t } = useTranslation('basket');

  const productAssignmentColumns = [
    {
      title: t('product'),
      dataIndex: 'product',
      key: 'product',
      width: 180,
      render: (product: string) => <span className="text-gray-700">{product || 'N/A'}</span>,
    },
    {
      title: t('quantityAssignedPerProduct'),
      dataIndex: 'quantityAssigned',
      key: 'quantityAssigned',
      width: 160,
      render: (quantity: number | string) => (
        <span className="text-gray-700">{quantity ?? 'N/A'}</span>
      ),
    },
    {
      title: t('associatedProductPresentations'),
      dataIndex: 'associatedProductPresentations',
      key: 'associatedProductPresentations',
      width: 260,
      render: (presentations: Array<string | number>) =>
        presentations?.length ? (
          <div className="flex flex-wrap gap-1">
            {presentations.map((presentation, index) => (
              <Tag key={`${presentation}-${index}`} color="blue">
                {presentation}
              </Tag>
            ))}
          </div>
        ) : (
          <span className="text-gray-600">N/A</span>
        ),
    },
    {
      title: t('unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 120,
      render: (unit: string | null) => <span className="text-gray-700">{unit || 'N/A'}</span>,
    },
  ];

  return (
    <Modal
      open={open}
      title={t('productAssignments')}
      onCancel={onCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Table
        dataSource={getBasketProductAssignmentRows(basket)}
        columns={productAssignmentColumns}
        loading={loading}
        pagination={false}
        rowKey="key"
        scroll={{ x: 760 }}
        locale={{ emptyText: t('noProductAssignments') }}
      />
    </Modal>
  );
};

export default ProductAssignmentsModal;
